import { Fragment, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/intl";
import { errMsg } from "./kpiFormat";

const LeadRows = ({ leads }) => leads.map((l, i) => (
  <tr key={`${l.client_id}-${i}`} className="bg-zinc-50/70 text-xs" data-testid={`kpi-form-lead-${l.form_id}-${i}`}>
    <td className="py-1.5 pl-8 pr-3 text-zinc-700">
      {l.client_name || l.client_email || l.client_id}
      <span className="ml-2 text-zinc-400">{fmtDate(l.date)} · {l.paid ? "paid" : "organic"}{l.campaign ? ` · ${l.campaign}` : ""}</span>
    </td>
    <td colSpan={4} className="py-1.5 pr-3 text-zinc-600">
      {l.purchases.length ? l.purchases.map((p) => `${p.kind}: ${p.product} (${fmtDate(p.at)})`).join(" · ")
        : <span className="text-zinc-400">Nothing yet{new Date(l.window_ends) > new Date() ? ` · can still buy until ${fmtDate(l.window_ends)}` : " · buying window closed"}</span>}
    </td>
  </tr>
));

const FormRow = ({ f, leads, open, onToggle }) => (
  <Fragment>
    <tr className="border-t border-zinc-100" data-testid={`kpi-form-row-${f.id}`}>
      <td className="py-2 pr-3">
        <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 text-left text-zinc-800 transition-colors hover:text-blue-700"
          data-testid={`kpi-form-toggle-${f.id}`}>
          <ChevronRight className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${open ? "rotate-90" : ""}`} />{f.name}
        </button>
      </td>
      <td className="py-2 pr-3 font-mono-num">{f.leads}</td>
      <td className="py-2 pr-3 font-mono-num">{f.intro_pack}</td>
      <td className="py-2 pr-3 font-mono-num">{f.membership}</td>
      <td className="py-2 pr-3 font-mono-num font-semibold text-zinc-900" data-testid={`kpi-form-pct-${f.id}`}>
        {f.pct.toFixed(1)}%<span className="ml-1.5 font-normal text-zinc-500">({f.converted} of {f.leads})</span>
        {f.window_open > 0 && <span className="ml-2 text-2xs font-normal uppercase tracking-wider text-amber-700">{f.window_open} still open</span>}
      </td>
    </tr>
    {open && <LeadRows leads={leads} />}
  </Fragment>
);

/** Live: new leads from each Facebook form this week and what they've bought so far within their buying window. */
export const FormConversion = ({ week }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => {
    setD(null);
    api.get(`/kpi/reports/${week}/form-conversion`, { timeout: 120000 }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [week]);
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (err) return <p className="text-sm text-red-600" data-testid="kpi-form-conversion-error">{err}</p>;
  if (!d) return <div className="h-20 animate-pulse rounded bg-zinc-100" data-testid="kpi-form-conversion-loading" />;
  return (
    <div className="space-y-2" data-testid="kpi-form-conversion">
      <p className="text-xs text-zinc-500">
        New leads who sent a Facebook form this week (paid and organic) and what they've bought so far — intro offers, class packs
        and memberships within their buying window. Live as of {fmtDateTime(d.as_of)}, so it keeps growing; not part of the approved figures.
      </p>
      {!d.forms.length ? <p className="text-sm text-zinc-500" data-testid="kpi-form-conversion-empty">No new leads came from a Facebook form this week.</p> : (
        <table className="w-full max-w-4xl text-sm">
          <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
            <th className="pb-2 pr-4">Form</th><th className="pb-2 pr-4">Leads</th><th className="pb-2 pr-4">Bought intro / pack</th>
            <th className="pb-2 pr-4">Bought membership</th><th className="pb-2 pr-4">Converted so far</th></tr></thead>
          <tbody>
            {d.forms.map((f) => <FormRow key={f.id} f={f} leads={d.leads.filter((l) => l.form_id === f.id)} open={open.has(f.id)} onToggle={() => toggle(f.id)} />)}
          </tbody>
        </table>
      )}
    </div>
  );
};
